import { ITela } from '../../hooks/useTelas';

// Como a Central agrupa e nomeia telas e ações.

export interface IModulo {
  chave: string;
  nome: string;
  cor: string;
  fundo: string;
}

const MODULOS: (IModulo & { prefixos: string[] })[] = [
  {
    chave: 'fiscal',
    nome: 'Fiscal',
    cor: '#075985',
    fundo: '#DFF3FF',
    prefixos: ['/modulesHome/supervisor', '/modulesHome/fiscal', '/modulesHome/taxPlanning'],
  },
  {
    chave: 'contabil',
    nome: 'Contábil',
    cor: '#9A3412',
    fundo: '#FFEDD5',
    prefixos: ['/modulesHome/accountingConciliation', '/modulesHome/contabil'],
  },
  {
    chave: 'auditoria',
    nome: 'Auditoria Inteligente',
    cor: '#5B21B6',
    fundo: '#EDE9FE',
    prefixos: ['/modulesHome/auditoria-inteligente'],
  },
  {
    chave: 'integracao',
    nome: 'Integração',
    cor: '#0F766E',
    fundo: '#CCFBF1',
    prefixos: ['/modulesHome/integration', '/breaks', '/chatPro'],
  },
  {
    chave: 'dp',
    nome: 'Departamento Pessoal',
    cor: '#155E75',
    fundo: '#CFFAFE',
    prefixos: ['/modulesHome/personnelDepartment'],
  },
  {
    chave: 'financeiro',
    nome: 'Financeiro',
    cor: '#6D28D9',
    fundo: '#EDE9FE',
    prefixos: ['/modulesHome/financeiro'],
  },
  {
    chave: 'eventos',
    nome: 'Eventos',
    cor: '#9A3412',
    fundo: '#FFEDD5',
    prefixos: ['/eventos', '/eventos-acoes', '/kanban/eventos', '/sympla'],
  },
  { chave: 'kanban', nome: 'Kanban', cor: '#1E40AF', fundo: '#DBEAFE', prefixos: ['/kanban'] },
  {
    chave: 'societario',
    nome: 'Societário',
    cor: '#9D174D',
    fundo: '#FCE7F3',
    prefixos: ['/societario'],
  },
  {
    chave: 'empresas',
    nome: 'Empresas',
    cor: '#334155',
    fundo: '#E2E8F0',
    prefixos: ['/empresas', '/dossie-empresa'],
  },
  {
    chave: 'whatsapp',
    nome: 'WhatsApp',
    cor: '#166534',
    fundo: '#DCFCE7',
    prefixos: ['/whatsapp'],
  },
  {
    chave: 'orquestrador',
    nome: 'Orquestrador e bots',
    cor: '#3730A3',
    fundo: '#E0E7FF',
    prefixos: [
      '/monitoramento',
      '/orquestrador',
      '/analise-bots',
      '/configuracao-bots',
      '/automacao',
    ],
  },
  {
    chave: 'configuracoes',
    nome: 'Configurações',
    cor: '#3730A3',
    fundo: '#E0E7FF',
    prefixos: ['/configuracoes', '/perfil', '/seguranca', '/sessoes'],
  },
];

const OUTRAS: IModulo = { chave: 'outras', nome: 'Outras telas', cor: '#475569', fundo: '#F1F5F9' };

export function moduloDaTela(tela: Pick<ITela, 'path'>): IModulo {
  // o prefixo mais longo vence (/kanban/eventos é Eventos, /kanban é Kanban)
  let melhor: (typeof MODULOS)[0] | undefined;
  let tamanho = -1;
  MODULOS.forEach(m =>
    m.prefixos.forEach(p => {
      if ((tela.path === p || tela.path.startsWith(`${p}/`)) && p.length > tamanho) {
        melhor = m;
        tamanho = p.length;
      }
    }),
  );
  return melhor ?? OUTRAS;
}

export const ordemDosModulos = [...MODULOS.map(m => m.chave), OUTRAS.chave];

/** Ações com coluna própria na grade. As demais vão para "Outras ações". */
export const ACOES_PADRAO: { acao: string; rotulo: string }[] = [
  { acao: 'view', rotulo: 'Ver' },
  { acao: 'create', rotulo: 'Criar' },
  { acao: 'edit', rotulo: 'Editar' },
  { acao: 'delete', rotulo: 'Excluir' },
];

const ROTULOS: Record<string, string> = {
  view: 'Ver',
  create: 'Criar',
  edit: 'Editar',
  delete: 'Excluir',
  show: 'Detalhes',
  // NFS-e
  emitir: 'Emitir',
  cancelar: 'Cancelar',
  certificado: 'Certificado',
  configurar: 'Configurar',
  // WhatsApp — conversas
  assumir_fila: 'Assumir da fila',
  assumir_departamento: 'Assumir do departamento',
  assumir_todos: 'Assumir qualquer conversa',
  transferir_proprio: 'Transferir as próprias',
  transferir_departamento: 'Transferir do departamento',
  transferir_todos: 'Transferir qualquer conversa',
  responder: 'Responder',
  nota_interna: 'Nota interna',
  finalizar: 'Finalizar',
  abrir_chamado: 'Abrir conversa',
  editar: 'Editar',
  visualizar: 'Ver',
  criar: 'Criar',
  editar_proprio: 'Editar as próprias',
  editar_todos: 'Editar todas',
  excluir: 'Excluir',
  mover: 'Mover entre colunas',
  iniciar_conversa: 'Iniciar conversa',
  // Eventos
  ver_painel_executivo: 'Ver Painel Executivo',
  ver_financeiro: 'Ver Financeiro',
  receber_aviso_assinatura: 'Receber aviso de assinatura',
  // Kanban de Eventos
  retroceder_card: 'Voltar card de coluna',
  editar_coluna: 'Editar coluna padrão',
  excluir_coluna: 'Excluir coluna padrão',
  mover_coluna: 'Mover coluna padrão',
  editar_card: 'Editar card sem vínculo',
  pular_coluna: 'Pular coluna',
  avancar_card: 'Avançar card',
  criar_card: 'Criar card',
};

export const rotuloDaAcao = (acao: string) =>
  ROTULOS[acao] ?? acao.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());

/** Telas cujos nomes no catálogo vieram do nome técnico da rota. */
export const nomeDaTela = (tela: Pick<ITela, 'nome' | 'codigo'>) =>
  tela.nome
    .replace(/^integration /, 'Integração: ')
    .replace(/^fiscal /, 'Fiscal: ')
    .replace(/^Modulos do dashboard /, 'Painel: ');

/** O que cada permissão geral libera (as que não são de uma tela). */
export const DESCRICAO_GERAL: Record<string, string> = {
  usuarios_alterar_nivel: 'Mudar o nível de outro colaborador.',
  usuarios_editar_imagem_outros: 'Trocar ou remover a foto de outro usuário.',
  usuarios_gerenciar_campos: 'Criar e editar os campos personalizados de usuários.',
  empresas_gerenciar_campos: 'Criar e editar os campos personalizados de empresas.',
  links_temporarios_gerenciar: 'Gerar link de acesso externo para um cliente.',
  solicitar_acesso_conta: 'Pedir para entrar como outro usuário, com aprovação dele.',
  kanban_ver_todos_os_cards:
    'Nos quadros (Kanban), ver todos os cards e não só os em que é responsável. Os cards de empresa fora da carteira continuam escondidos.',
};

/** Códigos que não aparecem em "Permissões gerais": a carteira tem aba própria e
 * os kanban_eventos_* antigos não valem mais nada (o backend usa as ações da tela). */
export const geralVisivel = (codigo: string) =>
  codigo !== 'visualizar_todos_grupos_empresas' && !codigo.startsWith('kanban_eventos_');

export const CODIGO_VER_TODAS_CARTEIRAS = 'visualizar_todos_grupos_empresas';
